import { logReport, validate, matchers } from "../validate-props";

/**
 * Loop through object prop and check if value is possible **boolean or number** force these types
 *
 * Ex: {maxChar:"10",show:"true"} => {maxChar:10, show:true}
 *
 * It works with nested objects too.
 *
 * @param {Object} object
 * @returns {Object}
 */
const enforceType = (object) => {
    return (
        matchers.object(object) &&
        Object.keys(object).reduce((acc, propName) => {
            const propVal = object[propName];
            if (typeof propVal === "boolean") {
                acc[propName] = propVal;
            } else if (propVal === "true" || propVal === "false") {
                acc[propName] = propVal === "true";
            } else if (Array.isArray(propVal)) {
                acc[propName] = propVal;
            } else if (matchers.date(propVal)) {
                acc[propName] = propVal;
            } else if (!isNaN(Number(propVal))) {
                acc[propName] = Number(propVal);
            } else if ((propVal !== undefined || propVal !== null) && matchers.object(propVal)) {
                acc[propName] = enforceType(propVal);
            } else {
                acc[propName] = propVal;
            }
            return acc;
        }, {})
    );
};

/**
 * Helper function that normalizes the routine of
 * checks and routines every DDS component implements.
 *
 * @param {Object} config object with the following props
 * config.componentName {String} Required
 * config.element {HTMLElement} Required
 * config.props {Object} initialization props from javascript
 * config.defaultProps {Object} object with default props to merge into props and dataset
 * config.schema {Object} object with property matchers (uses validate-props helper)
 * config.validateHTML {Function} get called with **element** and return a boolean
 * @returns [success, mergedProps]
 */
export default function (config = {}) {
    if (!matchers.object(config)) {
        return [false, null];
    }
    const { componentName, element, schema, validateHTML } = config;
    let { defaultProps, props } = config;
    //
    if (!matchers.string(componentName)) {
        console.error(`${componentName} initialization failed. Missing componentName`);
        return [false, null];
    }
    if (!matchers.htmlElement(element)) {
        console.error(`${componentName} initialization failed. Missing element(s)`);
        return [false, null];
    }
    if (matchers.object(element[componentName])) {
        console.warn(`${componentName} initialization failed. Already initialized`);
        return [false, null];
    }

    if (!matchers.object(defaultProps)) defaultProps = {};
    if (!matchers.object(props)) props = {};

    // Blend everything before validation
    let mergedProps = Object.assign({}, defaultProps, props, element.dataset);
    mergedProps = enforceType(mergedProps);

    if (matchers.object(schema)) {
        const [isValidated, report] = validate(mergedProps, schema);
        if (!isValidated) {
            logReport(report);
            return [false, null];
        }
    }

    // Check to see if ValidateHTML has been passed
    let validElements;
    if (!matchers.undefined(validateHTML)) {
        if (matchers.function(validateHTML)) {
            try {
                validElements = validateHTML(element, mergedProps);
                if (!validElements) {
                    console.warn(
                        `${componentName} initialization failed. Missing required element(s)`
                    );
                    return [false, null];
                }
            } catch (err) {
                console.error(`${componentName} got an error running validateHTML`, err);
                return [false, null];
            }
        } else {
            console.warn(`${componentName} validateHTML is not a function`);
            return [false, null];
        }
    }

    return [true, mergedProps, validElements];
}
